<template>
  <div ref="mainElement" class="element-list">
    <button-list
      :items="elementItems"
      :order="store.order"
      :sortable="true"
      @select="select($event)"
      @update:order="store.saveOrder($event)"
      @contextmenu="onContextMenu($event)"
      @delete="store.delete({ id: $event.id })"
      @hoveron="highlightElement($event)"
      @hoveroff="removeHighlight($event)"
    >
      <template #default="{ item }">
        <template v-if="item.title">
          {{ item.title }}
        </template>
        <span v-else class="placeholder-text">
          {{ $t("labels.unnamed") }}
        </span>
      </template>
      <template #icons="{ item: element }">
        <!-- <button v-if="nodes[element.id]" type="button" class="button-icon button-item__icon" @click="focusElement(element.id)">
          <lucide-icon name="locate" />
        </button> -->
        <button v-if="!nodes[element.id]" type="button" class="button-icon button-item__icon" @click="instantiateElement(element)" :title="$t('actions.placeElementInScene')">
          <lucide-icon name="circle-plus" />
        </button>
        <button class="button-icon button-item__icon" type="button" @click="deleteElement(element.id)" :title="$t('actions.deleteElement')">
          <lucide-icon name="trash-2" />
        </button>
      </template>
      <!-- <template #after="{ item: element }">
        <button v-if="nodes[element.id]" type="button" class="button-icon" @click="focusElement(element.id)">
          <lucide-icon name="locate" />
        </button>
        <button v-else type="button" class="button-icon" @click="instantiateElement(element)">
          <lucide-icon name="circle-plus" />
        </button>
      </template> -->
      <template #after-list>
        <input-create
          @submit="createNewElement"
        />
      </template>
    </button-list>

    <context-menu
      v-if="contextMenuData.element"
      :x="contextMenuData.x"
      :y="contextMenuData.y"
      @close="contextMenuData.element = null"
    >
      <button
        v-if="!nodes[contextMenuData.element.id]"
        type="button"
        class="context-menu__entry"
        @click="instantiateElement(contextMenuData.element as ElementItem<T>)"
      >
        <lucide-icon name="circle-plus" class="mr-1" />
        <span>{{ $t("actions.placeElementInScene") }}</span>
      </button>
      <button
        v-else
        type="button"
        class="context-menu__entry"
        @click="removeFromScene(contextMenuData.element as ElementItem<T>)"
      >
        <lucide-icon name="x" class="mr-1" />
        <span>{{ $t("actions.removeElementFromScene") }}</span>
      </button>
      <button
        type="button"
        class="context-menu__entry"
        @click="duplicateElement(contextMenuData.element as ElementItem<T>)"
      >
        <lucide-icon name="copy" class="mr-1" />
        <span>{{ $t("actions.duplicate") }}</span>
      </button>
      <button
        type="button"
        class="context-menu__entry context-menu__entry--danger"
        @click="onContextMenuDelete(contextMenuData.element as ElementItem<T>)"
      >
        <lucide-icon name="trash-2" class="mr-1" />
        <span>{{ $t("actions.deleteElement") }}</span>
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts" generic="T extends 'target' | 'resource' | 'index'">
import { reactive, nextTick, ref, computed } from "vue";
import { useI18n } from "vue-i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonList from "@core/ui/ButtonList.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import InputCreate from "@core/input/InputTextCreate.vue";

import { useSceneTree } from "../scene/sceneTree";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSceneElementStore } from "../sceneElement/sceneElements.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { useElements } from "./elements";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";

interface Props {
  type: T;
  highlight?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  highlight: false
});

type ElementType = "target" | "resource" | "index";
type ElementItemMap = {
  target: TargetItem;
  resource: ResourceItem;
  index: IndexItem;
};
type ElementItem<T extends ElementType = ElementType> = ElementItemMap[T];

const elements = useElements();
const sceneTree = useSceneTree();
const selectionStore = useSelectionStore();
const sceneElementStore = useSceneElementStore();

const mainElement = ref<HTMLElement>();

const store = computed(() => elements.storeByType[props.type]);
const elementItems = computed((): Record<number, ElementItem<T>> => elements.byType[props.type] as Record<number, ElementItem<T>>);

async function createNewElement(title: string) {
  if (sceneElementStore.freeElementPosition === null) {
    console.error("No free position could be found.");
    return;
  }

  const element = await store.value.create({
    title
  });

  const { x, y } = sceneElementStore.freeElementPosition;

  sceneElementStore.create({
    x,
    y,
    width: 200,
    height: 90
  }, {
    elementUid: element.uid
  });
  
  nextTick(() => {
    const elInput = mainElement.value?.querySelector<HTMLButtonElement>(
      ".list-el-" + element.id
    );
    elInput?.focus();
  });
}

const nodes = computed(() => {
  return mapValues(elementItems.value, (element) => {
    return sceneTree.elementNodes.get(element.uid);
  })
});

async function instantiateElement(element: ElementItem<T>) {
  const freeElementPosition = sceneElementStore.freeElementPosition;
  if (freeElementPosition == null) {
    console.error("No free position could be found.");
    return;
  }
  const node = await sceneElementStore.create({
    x: freeElementPosition.x,
    y: freeElementPosition.y,
    width: 200,
    height: 90
  }, {
    elementUid: element.uid
  });
  selectionStore.select(node);
}

async function removeFromScene(element: ElementItem<T>) {
  const node = sceneTree.elementNodes.get(element.uid);
  if (!node) {
    console.warn("Element is not in the scene", element);
    return;
  }
  useSceneNodesStore().delete({ id: node.id }, { deleteElement: false });
}

// function focusElement(elementId: number) {
//   const node = nodes.value[elementId];
//   if (!node) {
//     console.warn("No node found for element", elementId);
//     return;
//   }
//   sceneTree.centerOnNode(node.uid);
//   selectionStore.select(node);
// }

function deleteElement(elementId: number) {
  store.value.delete({ id: elementId });
}

// ==============================
// Copy

const { t } = useI18n();

function duplicateElement(element: ElementItem<T>) {
  store.value.duplicate(element.id, {
    title: `${element.title} ${t("labels.copySuffix")}`
  });
}

// ==============================
// Selection

function select(element: ElementItem<T>) {
  const node = nodes.value[element.id];
  if (node) {
    selectionStore.select(node);
  } else {
    selectionStore.select(element);
  }
}

function highlightElement(element: ElementItem<T>) {
  if (!props.highlight) return;
  if (!nodes.value[element.id]) {
    return;
  }
  selectionStore.setHighlight(element);
}

function removeHighlight(element: ElementItem<T>) {
  if (!props.highlight) return;
  selectionStore.removeHighlight(element);
}

// ==============================
// Context Menu

const contextMenuData = reactive({
  visible: false,
  x: 0,
  y: 0,
  element: null as ElementItem | null
});

function onContextMenu(event: MouseEvent & { item: ElementItem<T> | null }) {
  contextMenuData.x = event.clientX;
  contextMenuData.y = event.clientY;
  contextMenuData.element = event.item;
}

function onContextMenuDelete(element: ElementItem | null) {
  if (element == null) {
    console.warn("no element selected");
    return;
  }
  store.value.delete({ id: element.id });
  contextMenuData.element = null;
}
</script>

<style lang="scss">
.element-list {
  padding: 0.5em 0;
}
</style>
